/* GASSISA
=========================================================================================
* Descripción: Menú de acciones del módulo de etiquetado
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 29/01/2025 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 22/03/2026 | Lic. José Luis Aragón Cervantes  | Simplificación de botones
*      3       | 27/04/2026 | Lic. José Luis Aragón Cervantes  | Menú hamburguesa para etiquetar
*      4       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Eliminaciones en submenú de otras acciones
* ---------------------------------------------------------------------------------------
*/

import { IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from '@mui/icons-material/Print';
import HomeIcon from '@mui/icons-material/Home';
import InventoryIcon from '@mui/icons-material/Inventory';
import LabelIcon from '@mui/icons-material/Label';
import RefreshIcon from '@mui/icons-material/Refresh';

import { Fragment, useState } from "react";
import { useInventarios } from "../../context/Inventarios/InventariosContext";
import { useSesion } from "../../context/System/SesionContext.js";
import { useDoubleClickPrevention } from '../../hooks/useDoubleClickPrevention';
import MnuDangerActionsMenu from "../utils/MnuDangerActionsMenu";

export default function MnuEtiquetadoActions({ onRefresh, onBuscarArticulos }) {
    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();
    const { parametrosCajaForm } = useSesion();
    const {
        handleClickRegresarMainDashboard,
        handleClickNuevoEtiquetado,
        handleEliminaPartidaEtiquetado,
        handleEliminaEtiquetadoCompleto,
        handleImprimirEtiquetas,
        dataArticuloEtiquetar,
        articuloEtiquetar,
        statusEnableFieldsEtiquetado,
        cantidadesEtiquetado,
        almacenExhibidorEtiquetado,
        openModalConfirmIntegrar,
        setOpenModalConfirmIntegrar,
        openModalConfirmSoloEtiquetado,
        setOpenModalConfirmSoloEtiquetado,
        estadoImpresoraEtiqueta,
    } = useInventarios();

    const [anchorElMenu, setAnchorElMenu] = useState(null);
    const openMenu = Boolean(anchorElMenu);

    const hasData = dataArticuloEtiquetar && dataArticuloEtiquetar.length > 0;
    const impresoraBloqueada = !estadoImpresoraEtiqueta?.canPrint;
    const motivoBloqueoImpresora = estadoImpresoraEtiqueta?.verificando
        ? "Verificando impresora..."
        : (estadoImpresoraEtiqueta?.message || "Impresora no disponible");

    const handleClickMenu = (event) => {
        setAnchorElMenu(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorElMenu(null);
    };

    const handleImprimirConIntegracion = async () => {
        handleCloseMenu();
        if (!hasData) return;
        setOpenModalConfirmIntegrar(true);
    };

    const handleImprimirSinIntegracion = async () => {
        handleCloseMenu();
        if (!hasData) return;
        setOpenModalConfirmSoloEtiquetado(true);
    };

    return (
        <Fragment>
            <Tooltip title={"Home"}>
                <IconButton onClick={() => executeWithProtection(handleClickRegresarMainDashboard)} disabled={isProcessing}>
                    <HomeIcon className="icon" /><p className="icon">Salir</p>
                </IconButton>
            </Tooltip>

            <Tooltip title={"Nuevo"}>
                <span>
                    <IconButton 
                        onClick={() => executeWithProtection(handleClickNuevoEtiquetado)}
                        disabled={isProcessing}
                    >
                        <AddIcon className="icon" /><p className="icon">Nuevo</p>
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={"Refrescar datos"}>
                <span>
                    <IconButton
                        onClick={() => onRefresh && executeWithProtection(onRefresh)}
                        disabled={isProcessing || !onRefresh}
                    >
                        <RefreshIcon className="icon" /><p className="icon">Refrescar</p>
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={"Buscar artículos (Alt+F1)"}>
                <span>
                    <IconButton 
                        onClick={() => onBuscarArticulos && executeWithProtection(onBuscarArticulos)}
                        disabled={isProcessing || !onBuscarArticulos}
                    >
                        <SearchIcon className="icon" /><p className="icon">Buscar</p>
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Etiquetar">
                <span>
                    <IconButton 
                        onClick={handleClickMenu}
                        disabled={isProcessing || statusEnableFieldsEtiquetado || !hasData}
                    >
                        <PrintIcon className="icon" /><p className="icon">Etiquetar</p>
                    </IconButton>
                </span>
            </Tooltip>
            <Menu
                anchorEl={anchorElMenu}
                open={openMenu}
                onClose={handleCloseMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1,
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
            >
                {impresoraBloqueada && (
                    <MenuItem disabled dense>
                        <ListItemText
                            primaryTypographyProps={{ variant: "caption", color: "error" }}
                        >
                            {`Impresora fuera de línea: ${motivoBloqueoImpresora}`}
                        </ListItemText>
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => executeWithProtection(handleImprimirConIntegracion)}
                    disabled={isProcessing || impresoraBloqueada}
                    title={impresoraBloqueada ? motivoBloqueoImpresora : "Generar etiquetado con ingreso a inventario"}
                >
                    <ListItemIcon>
                        <InventoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        Generar etiquetado con ingreso a inventario
                    </ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem
                    onClick={() => executeWithProtection(handleImprimirSinIntegracion)}
                    disabled={isProcessing || impresoraBloqueada}
                    title={impresoraBloqueada ? motivoBloqueoImpresora : "Generar solo etiquetado sin ingreso a inventario"}
                >
                    <ListItemIcon>
                        <LabelIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        Generar solo etiquetado sin ingreso a inventario
                    </ListItemText>
                </MenuItem>
            </Menu>
            <MnuDangerActionsMenu
                tooltip="Otras acciones"
                actions={[
                    {
                        label: "Eliminar partida",
                        onClick: () => handleEliminaPartidaEtiquetado(articuloEtiquetar),
                        disabled: statusEnableFieldsEtiquetado || !hasData || !articuloEtiquetar,
                    },
                    {
                        label: "Eliminar etiquetado",
                        onClick: handleEliminaEtiquetadoCompleto,
                        disabled: !hasData,
                    },
                ]}
            />
        </Fragment>
    );
}
